import classNames from 'classnames'
import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { TAG_CLICK_TOGGLE_HD_MAP } from '../../analytics/analytics.tagids.constants'
import { selectIsVectorMap } from '../../../store/map/map.selectors'
import { switchMapType } from '../../../store/map/mapSlice'
import { UI_ELEMENTS } from '../../../store/ui/ui.constants'
import { selectShouldDisplayMapTools } from '../../../store/ui/ui.selectors'
import useConfigureAndSendTag from '../../analytics/useConfigureAndSendTag'
import { isGE } from '../../Device'
import { ACTION, CLICK_TAG } from '../../analytics/ClickTagsService'
import Modal from '../../genericComponents/Modal'
import cross from '../../genericComponents/assets/cross.svg'
import layers from '../../genericComponents/assets/layers.svg'
import T from '../../i18n/T'
import SVGIcon from '../../svg/SVGIcon'
import useUIDisplay from '../../ui/useUIDisplay'
import SwitchInput from '../../utils/SwitchInput'
import { withEverySelectorTruthy } from '../../utils/react-redux'
import useEventCallback from '../../utils/useEventCallback'
import useTooltip from '../tools/useTooltip'
import HdTooltip from '../vector/HdTooltip'
import { ModeLayerButton, ThemeLayerButton } from './LayerButton'
import s from './LayersButtons.less'
import informationImg from './information.png'

import { hideUiMapTooSlowTooltip } from '../../../store/ui/uiSlice'
import { useMapModeButtons, useMapThemeButtons } from './LayersButtonsConfig'

const Buttons = ({ isOpened, isClosing, closeModes }) => {
  const isVecto = useSelector(selectIsVectorMap)

  const { tooltip: hdTooltipDisplayed, setTooltip: setHdTooltip } = useTooltip({ ge: true })
  const dispatch = useDispatch()
  const configureAndSendTag = useConfigureAndSendTag()

  const switchHdTooltip = useEventCallback(() => setHdTooltip(!hdTooltipDisplayed), [setHdTooltip, hdTooltipDisplayed])

  const switchHighDefMap = useCallback(() => {
    dispatch(switchMapType())
    configureAndSendTag({
      type: TAG_CLICK_TOGGLE_HD_MAP,
      tagInfo: { tag: CLICK_TAG.XTC_CARTE_HD_OFF, [ACTION]: isVecto ? 'HD_off' : 'HD_on' }
    })
  }, [switchMapType])

  const themeButtons = useMapThemeButtons()
  const modeButtons = useMapModeButtons()

  return (
    <div className={classNames(s.buttons, isOpened && s.opened, isClosing && s.closing)}>
      <div className={s.mainTitle}>
        <T id={'map.tools.layers.tooltip'} />
      </div>
      <div className={classNames(s.closeButton)} onClick={() => closeModes()}>
        <div className="icon">
          <SVGIcon url={cross} />
        </div>
      </div>
      {isGE() && (
        <div className={s.buttonsSection}>
          <div className={s.highdef}>
            <div className={s.highDefButton}>
              <SwitchInput defaultValue={isVecto} onChange={switchHighDefMap} />
              <p className={s.highdefLabel}>
                <T id={'map.tools.layers.highdef.label'} />
              </p>
            </div>
            <img src={informationImg} className={s.informationIcon} onClick={switchHdTooltip} />
            {hdTooltipDisplayed && <HdTooltip onClick={switchHdTooltip} />}
          </div>
        </div>
      )}
      <div className={s.buttonsSection}>
        <div className={s.title}>
          <T id={'map.tools.layers.theme'} />
        </div>
        <div className={s.buttonsList}>
          {themeButtons.map(({ type, key, icon, availableInRaster }) => (
            <ThemeLayerButton key={key} id={key} icon={icon} disabled={!isVecto && !availableInRaster} />
          ))}
        </div>
      </div>
      <div className={s.buttonsSection}>
        <div className={s.title}>
          <T id={'map.tools.layers.mode'} />
        </div>
        <div className={s.buttonsList}>
          {modeButtons.map(({ key, icon, isEventMode, availableInRaster, label, ariaLabel, tooltipLabel }) => (
            <ModeLayerButton
              key={key}
              id={key}
              icon={icon}
              label={label}
              ariaLabel={ariaLabel}
              tooltipLabel={tooltipLabel}
              disabled={!isVecto && !availableInRaster}
              isEventMode={isEventMode}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const LayersButtons = () => {
  const dispatch = useDispatch()

  const [display, setDisplay] = useState('close')

  const { tooltip, setTooltip, mouseListeners } = useTooltip({ ge: true })
  const isOpened = display === 'opened'
  const isClosing = display === 'closing'
  const { showUI, hideUI } = useUIDisplay(UI_ELEMENTS.MAP_TOOLS_PE)

  const closeModes = useCallback(() => {
    setDisplay('closing')
    setDisplay('closed')
    hideUI()
  }, [setDisplay, hideUI])

  const onOpenClick = useCallback(() => {
    setDisplay('opened')
    setTooltip(false)
    showUI()
    dispatch(hideUiMapTooSlowTooltip())
  }, [setDisplay, setTooltip, showUI, dispatch])

  return (
    <div className={classNames(s.root, isOpened && s.opened, isClosing && s.closing)}>
      {isGE() ? (
        <Buttons closeModes={closeModes} isClosing={isClosing} isOpened={isOpened} />
      ) : (
        <Modal
          modalId={UI_ELEMENTS.MAP_TOOLS_PE}
          isOpen={isOpened}
          style={{
            bottom: '0',
            top: 'auto',
            width: '100%',
            transform: 'translate(-50%, 0%)',
            borderBottomRightRadius: '0',
            borderBottomLeftRadius: '0'
          }}
          overlayStyle={{
            zIndex: 180,
            backgroundColor: 'rgba(0,0,0, 0.5)'
          }}
          onRequestClose={closeModes}
          shouldCloseOnOverlayClick
        >
          <Buttons closeModes={closeModes} isClosing={isClosing} isOpened={isOpened} />
        </Modal>
      )}
      <div className={classNames(s.button, s.openButton)} onClick={onOpenClick} {...mouseListeners}>
        <T id={'map.tools.layers.maps'} />
        <div className="icon">
          <SVGIcon url={layers} />
        </div>
      </div>
      <span className={classNames(s.tooltip, tooltip && 'active')}>
        <T id={'map.tools.layers.tooltip'} />
      </span>
    </div>
  )
}

export default withEverySelectorTruthy(selectShouldDisplayMapTools)(LayersButtons)
